// export default MultiStepImport;
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  LinearProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Settings as ConfigIcon,
  MapOutlined as MapIcon,
  CheckCircle as SuccessIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";

import { useAuthDetails } from "../../Common/cookiesHelper";
import { getSessionAuthHeader, withSessionFields } from "../../Common/sessionAuth";
import {
  BASE_URL,
  IMPORT_DATA,
  IMPORT_DATA_ADVANCE,
  BUCKET_BASE_URL,
  UPDATE_IMPORT_PREFS,
  GET_IMPORT_PREFS,
} from "../../Constants/Config";
import axios from "axios";
import { ToastifyAlert } from "../../CommonComponents/ToastifyAlert";

const MultiStepImport = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processedFile, setProcessedFile] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [failedRows, setFailedRows] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const hasShownAutoMapMessage = useRef(false);
  const hasShownStandardLayoutMessage = useRef(false);
  const delimiterErrorShown = useRef(false);
  const processedFiles = useRef(new Set());
  const [duplicateHandling, setDuplicateHandling] = useState("skip");
  const [overwrite, setOverWrite] = useState("productandvariantid");
  const [encoding, setEncoding] = useState("UTF-8");
  const [del, setDelimeter] = useState("comma");
  const previousDelimiter = useRef(del);
  const [savePreferences, setSavePreferences] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [showSkippedDetails, setShowSkippedDetails] = useState(false);
  const [showUnmappedDetails, setShowUnmappedDetails] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [delimiterError, setDelimiterError] = useState("");
  const [fieldValidationWarnings, setFieldValidationWarnings] = useState({});
  const isMenuOpenRedux = useSelector((state) => state.NavBarToggle.isMenuOpen);

  const fileInputRef = useRef(null);
  const { LoginGetDashBoardRecordJson, userTypeData } = useAuthDetails();
  const merchant_id = LoginGetDashBoardRecordJson?.data?.merchant_id;

  // Configuration options
  const encodings = [
    { value: "UTF-8", label: "UTF-8 (Unicode)" },
    { value: "UTF-16", label: "UTF-16 (Unicode)" },
    { value: "ISO-8859-1", label: "ISO-8859-1" },
    { value: "ISO-8859-2", label: "ISO-8859-2" },
    // { value: "ISO-8859-9", label: "ISO-8859-9 (Turkish)" },
    // { value: "GB2312", label: "GB2312 (Simplified Chinese)" },
    // { value: "Big5", label: "Big5 (Traditional Chinese)" },
    // { value: "Shift_JIS", label: "Shift_JIS (Japanese)" },
  ];

  const delimiters = [
    { value: "comma", label: "Comma (,)" },
    { value: "semi-colon", label: "Semi-Colon (;)" },
  ];

  const overWrites = [
    { value: "productandvariantid", label: "Product ID & Variant ID" },
  ];

  const steps = [
    { label: "Configure", icon: <ConfigIcon /> },
    { label: "Map Fields", icon: <MapIcon /> },
    { label: "Upload", icon: <UploadIcon /> },
  ];

  const availableFields = [
    "ProductTitle",
    "ProductDescription",
    "Category",
    "Price",
    "ComparePrice",
    "CostPrice",
    "CustomCode1",
    "CustomCode2",
    "CustomCode3",
    "CustomCode4",
    "CustomCode5",
    "CustomCode6",
    "CustomCode7",
    "CustomCode8",
    "CustomCode9",
    "CustomCode10",
    "CustomCode11",
    "CustomCode12",
    "CustomCode13",
    "CustomCode14",
    "CustomCode15",
    "ReorderPoint",
    "RestockAmount",
    "checkId",
    "Disable",
    "FoodStampable",
    "Tags",
    "Brand",
    "Variant1Name",
    "Variant1Value",
    "Variant2Name",
    "Variant2Value",
    "Variant3Name",
    "Variant3Value",
    "Quantity",
    "TrackQuantity",
    "ContinueSelling",
    "OnlinePickup",
    "OnlineDelivery",
    "DefaultTax",
    "SKU",
    "UPC",
    "FinalOutputUPC",
  ];

  const expectedHeaders = [...availableFields];

  const csvFieldMapping = Object.fromEntries(
    availableFields.map((field) => [field, field])
  );

  // Normalize header for comparison
  const normalizeHeader = (h) =>
    (h ?? "")
      .toString()
      .replace(/^["']|["']$/g, "")
      .trim()
      .toLowerCase();

  // Check if headers match expected headers exactly
  const isExactStandardHeader = (uploadedHeaders = []) => {
    if (
      !Array.isArray(uploadedHeaders) ||
      uploadedHeaders.length !== expectedHeaders.length
    ) {
      return false;
    }
    return uploadedHeaders.every(
      (header, i) =>
        normalizeHeader(header) === normalizeHeader(expectedHeaders[i])
    );
  };

  // Validate field mapping based on data types and requirements
  const validateFieldMapping = (systemField, columnIndex, fileData) => {
    if (!fileData || fileData.length < 2 || columnIndex === undefined)
      return null;

    const dataRows = fileData.slice(1); // Skip header
    const columnData = dataRows
      .map((row) => row[columnIndex])
      .filter((val) => val && val.trim() !== "");

    if (columnData.length === 0) return null;

    const warnings = [];

    // Sample first few values for validation
    const sampleData = columnData.slice(0, Math.min(10, columnData.length));

    switch (systemField) {
      case "Price":
      case "ComparePrice":
      case "CostPrice":
        // Check if values are numeric
        const nonNumericCount = sampleData.filter((val) =>
          isNaN(parseFloat(val))
        ).length;
        if (nonNumericCount > 0) {
          warnings.push(
            `${nonNumericCount}/${sampleData.length} values are not numeric. Price fields should contain numbers.`
          );
        }
        break;

      case "ProductTitle":
        // Check if values are not empty
        const emptyCount = sampleData.filter(
          (val) => !val || val.trim() === ""
        ).length;
        if (emptyCount > 0) {
          warnings.push(
            `${emptyCount}/${sampleData.length} values are empty. Product Title is required.`
          );
        }
        break;

      case "Quantity":
        // Check if values are integers
        const nonIntegerCount = sampleData.filter(
          (val) => !Number.isInteger(parseFloat(val))
        ).length;
        if (nonIntegerCount > 0) {
          warnings.push(
            `${nonIntegerCount}/${sampleData.length} values are not whole numbers. Quantity should be integers.`
          );
        }
        break;

      case "TrackQuantity":
      case "ContinueSelling":
      case "OnlinePickup":
      case "OnlineDelivery":
      case "DefaultTax":
        // Check if values are boolean-like
        const validBooleans = ["true", "false", "1", "0", "yes", "no"];
        const nonBooleanCount = sampleData.filter(
          (val) => !validBooleans.includes(val.toLowerCase())
        ).length;
        if (nonBooleanCount > 0) {
          warnings.push(
            `${nonBooleanCount}/${sampleData.length} values are not boolean. Use TRUE/FALSE or 1/0.`
          );
        }
        break;
    }

    return warnings.length > 0 ? warnings : null;
  };

  // Parse entire CSV content to handle multi-line quoted fields and malformed quotes
  const parseCSVContent = (csvText, delimiter) => {
    const rows = [];
    let currentRow = [];
    let currentField = "";
    let inQuotes = false;
    let i = 0;

    // Normalize line endings
    csvText = csvText.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

    while (i < csvText.length) {
      const char = csvText[i];
      const nextChar = csvText[i + 1];
      const prevChar = i > 0 ? csvText[i - 1] : "";

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote within quoted field
          currentField += '"';
          i += 2;
          continue;
        } else if (
          !inQuotes &&
          (prevChar === delimiter || prevChar === "\n" || i === 0)
        ) {
          // Start of quoted field (only if at field beginning)
          inQuotes = true;
        } else if (
          inQuotes &&
          (nextChar === delimiter ||
            nextChar === "\n" ||
            nextChar === undefined)
        ) {
          // End of quoted field (only if at field end)
          inQuotes = false;
        } else {
          // Treat as regular character if quote is in middle of field
          currentField += char;
        }
      } else if (char === delimiter && !inQuotes) {
        // Field separator
        currentRow.push(currentField.replace(/^"|"$/g, "")); // Clean any stray quotes
        currentField = "";
      } else if (char === "\n" && !inQuotes) {
        // Row separator (only if not inside quotes)
        currentRow.push(currentField.replace(/^"|"$/g, "")); // Clean any stray quotes
        if (currentRow.some((field) => field.trim() !== "")) {
          rows.push(currentRow.map((field) => field.trim()));
        }
        currentRow = [];
        currentField = "";
      } else {
        // Regular character (including \n inside quotes)
        currentField += char;
      }

      i++;
    }

    // Handle last field/row
    if (currentField !== "" || currentRow.length > 0) {
      currentRow.push(currentField.replace(/^"|"$/g, "")); // Clean any stray quotes
      if (currentRow.some((field) => field.trim() !== "")) {
        rows.push(currentRow.map((field) => field.trim()));
      }
    }

    return rows;
  };

  // Robust CSV line parser
  const parseCSVLine = (line, delimiter) => {
    if (!line.trim()) return [];

    // Special handling for specific file format
    if (selectedFile?.name.includes("SampleProducts")) {
      const result = [];
      let current = "";
      let inQuotes = false;
      const expectedFields = 19;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === delimiter && !inQuotes) {
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }

      if (current.trim() !== "" || result.length < expectedFields - 1) {
        result.push(current.trim());
      }

      if (result.length < expectedFields && result.length > 0) {
        const parts = line.split(delimiter);
        if (parts.length >= expectedFields) {
          return parts.slice(0, expectedFields).map((field) => field.trim());
        }
      }

      return result;
    }

    // General CSV parsing
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }

    result.push(current);
    return result.map((field) =>
      field.startsWith('"') && field.endsWith('"')
        ? field.slice(1, -1).replace(/""/g, '"')
        : field
    );
  };

  // Quote-aware parser for the WHOLE file. Unlike splitting on "\n",
  // this keeps quoted fields that span multiple lines (e.g. a product
  // description with an embedded newline) inside a single field/record.
  const parseCSVRecords = (text, delimiter) => {
    const records = [];
    let row = [];
    let field = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (inQuotes) {
        if (char === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === delimiter) {
        row.push(field);
        field = "";
      } else if (char === "\n") {
        row.push(field);
        field = "";
        records.push(row);
        row = [];
      } else if (char === "\r") {
        // ignore (line endings already normalized)
      } else {
        field += char;
      }
    }

    // flush trailing field/row (file may not end with a newline)
    if (field !== "" || row.length > 0) {
      row.push(field);
      records.push(row);
    }

    return records;
  };

  // Detect delimiter from the first line
  const detectDelimiter = (firstLine) => {
    const delimiterCandidates = {
      ",": (firstLine.match(/,/g) || []).length,
      ";": (firstLine.match(/;/g) || []).length,
      "\t": (firstLine.match(/\t/g) || []).length,
      "|": (firstLine.match(/\|/g) || []).length,
    };

    let detectedDelimiter = ",";
    let maxCount = 0;
    for (const [delim, count] of Object.entries(delimiterCandidates)) {
      if (count > maxCount) {
        maxCount = count;
        detectedDelimiter = delim;
      }
    }

    return { detectedDelimiter, maxCount };
  };

  // Fetch preferences from database
  const fetchPreferencesFromDatabase = async () => {
    try {
      const formData = new FormData();
      formData.append("merchant_id", merchant_id);

      const response = await axios.post(BASE_URL + GET_IMPORT_PREFS, withSessionFields(formData), {
        headers: { "Content-Type": "multipart/form-data", ...getSessionAuthHeader() },
      });

      if (
        response.data.status === "success" &&
        response.data.import_preferences
      ) {
        const preferences = response.data.import_preferences;
        localStorage.setItem(
          "quickvee_import_preferences",
          JSON.stringify(preferences)
        );
        return preferences;
      }
      return null;
    } catch (error) {
      console.error("❌ Error fetching preferences from database:", error);
      return null;
    }
  };

  // Save preferences to database
  const savePreferencesToDatabase = async (preferences) => {
    try {
      const formData = new FormData();
      formData.append("merchant_id", merchant_id);
      formData.append("import_preferences", JSON.stringify(preferences));
      formData.append("action", UPDATE_IMPORT_PREFS);

      const response = await axios.post(
        BASE_URL + UPDATE_IMPORT_PREFS,
        withSessionFields(formData),
        {
          headers: { "Content-Type": "multipart/form-data", ...getSessionAuthHeader() },
        }
      );

      if (response.data.status === "success") {
        console.log("✅ Preferences saved to merchant record successfully!");
        return true;
      } else {
        console.error(
          "❌ Failed to save preferences to merchant record:",
          response.data.message
        );
        return false;
      }
    } catch (error) {
      console.error("❌ Error saving preferences to merchant record:", error);
      return false;
    }
  };

  // Save preferences to localStorage
  const savePreferencesToLocalStorage = async () => {
    const preferences = {
      encoding,
      delimiter: del,
      duplicateHandling,
      overwrite,
      fieldMapping,
      csvHeaders: fileData[0] || [],
      timestamp: new Date().toISOString(),
      version: "1.0",
    };

    try {
      localStorage.setItem(
        "quickvee_import_preferences",
        JSON.stringify(preferences)
      );
      if (savePreferences) {
        await savePreferencesToDatabase(preferences);
      }
    } catch (error) {
      console.error("❌ Error saving preferences:", error);
    }
  };

  // Load preferences
  const loadPreferences = async () => {
    try {
      const dbPreferences = await fetchPreferencesFromDatabase();
      if (dbPreferences) return dbPreferences;
      const saved = localStorage.getItem("quickvee_import_preferences");
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error("❌ Error loading preferences:", error);
      return null;
    }
  };

  useEffect(() => {
    const loadUserPreferences = async () => {
      const preferences = await loadPreferences();
      if (preferences) {
        setEncoding(preferences.encoding || "UTF-8");
        setDelimeter(preferences.delimiter || "comma");
        setDuplicateHandling(
          preferences.duplicateHandling === "overwrite" ? "overwrite" : "skip"
        );
        setOverWrite(preferences.overwrite || "productandvariantid");
        setFieldMapping(preferences.fieldMapping || {});
        setSavePreferences(true);
      }
    };
    loadUserPreferences();
  }, []);

  // Apply saved field mappings
  const applySavedFieldMappings = () => {
    try {
      const saved = localStorage.getItem("quickvee_import_preferences");
      if (saved && fileData.length > 0) {
        const preferences = JSON.parse(saved);
        const savedMappings = preferences.fieldMapping || {};
        const savedHeaders = preferences.csvHeaders || [];
        const newMappings = {};
        const csvHeaders = fileData[0] || [];

        Object.entries(savedMappings).forEach(
          ([systemField, savedColumnIndex]) => {
            if (systemField.includes(" ")) return;
            const savedColumnName = savedHeaders[savedColumnIndex];
            const currentColumnIndex = csvHeaders.findIndex(
              (header) =>
                header.trim().toLowerCase() ===
                savedColumnName?.trim().toLowerCase()
            );
            if (currentColumnIndex !== -1)
              newMappings[systemField] = currentColumnIndex;
          }
        );

        if (Object.keys(newMappings).length > 0) {
          setFieldMapping(newMappings);
          ToastifyAlert(
            `Applied ${Object.keys(newMappings).length} saved field mappings`,
            "success"
          );
        } else {
          ToastifyAlert("No matching columns found for saved mappings", "info");
        }
      }
    } catch (error) {
      console.error("Failed to apply saved field mappings:", error);
    }
  };

  useEffect(() => {
    if (fileData.length > 0) {
      const headers = fileData[0] || [];
      const autoMap = {};
      let mappedCount = 0;

      // Automatically map any matching fields (case-insensitive)
      availableFields.forEach((field) => {
        const normalizedField = normalizeHeader(field);
        const matchingIndex = headers.findIndex(
          (header) => normalizeHeader(header) === normalizedField
        );

        if (matchingIndex !== -1) {
          autoMap[field] = matchingIndex;
          mappedCount++;
        } else {
          autoMap[field] = ""; // Leave unmapped if no match found
        }
      });

      setFieldMapping(autoMap);

      // Check for missing required columns
      const requiredFields = ["ProductTitle", "Category", "Price", "SKU"];
      const missingRequiredFields = requiredFields.filter(
        (field) => autoMap[field] === undefined || autoMap[field] === ""
      );

      // Show appropriate success message
      if (mappedCount > 0 && !hasShownAutoMapMessage.current) {
        if (isExactStandardHeader(headers)) {
          ToastifyAlert(
            "Detected standard header layout. All fields mapped automatically.",
            "success"
          );
        } else {
          ToastifyAlert(
            `Auto-mapped ${mappedCount} matching field${mappedCount > 1 ? 's' : ''}.`,
            "success"
          );
        }
        hasShownAutoMapMessage.current = true;
      }

      // Show warning if required columns are missing
      if (missingRequiredFields.length > 0 && !hasShownAutoMapMessage.current) {
        ToastifyAlert(
          `Warning: Required columns not found in CSV: ${missingRequiredFields.join(", ")}. Please map them manually or these fields will be empty.`,
          "warning"
        );
        hasShownAutoMapMessage.current = true;
      }

      // If no fields were auto-mapped, try to apply saved mappings
      if (mappedCount === 0) {
        setTimeout(() => applySavedFieldMappings(), 100);
      }
    }
  }, [fileData]);

  // File handling
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleFileRemoval = () => {
    setSelectedFile(null);
    setFileData([]);
    setFailedRows([]);
    setFieldMapping({});
    setDelimiterError(""); // Clear delimiter error message
    setFieldValidationWarnings({}); // Clear validation warnings
    processedFiles.current.clear();
    hasShownAutoMapMessage.current = false;
    hasShownStandardLayoutMessage.current = false;
    delimiterErrorShown.current = false;
  };

  const handleFileSelection = async (file) => {
    const validTypes = [".csv", ".xlsx", ".xls"];
    const fileExt = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!validTypes.includes(fileExt)) {
      ToastifyAlert("Please select a CSV, XLS, or XLSX file", "error");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      ToastifyAlert("File size must be less than 20MB", "error");
      return;
    }

    setSelectedFile(file);
    setProcessedFile(file);
    hasShownAutoMapMessage.current = false;
    delimiterErrorShown.current = false;

    try {
      const result = await parseFilePreview(file, true, true);
      if (!result.success) {
        ToastifyAlert(
          "Invalid file format. Please check the file and try again.",
          "error"
        );
        handleFileRemoval();
      }
    } catch (error) {
      console.error("Error processing file:", error);
      ToastifyAlert("Error processing file. Please try again.", "error");
      handleFileRemoval();
    }
  };

  const parseFilePreview = useCallback(
    async (file, force = false, validateOnly = false) => {
      if (!file) {
        if (!validateOnly) {
          setFileData([]);
          setFieldMapping({});
        }
        return { success: false, error: "No file provided" };
      }

      if (!force && processedFiles.current.has(file.name) && !validateOnly) {
        return { success: true };
      }

      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text = e.target.result;
            if (!text || text.trim() === "") {
              const errorMsg = "File is empty. Please upload a file with data.";
              if (validateOnly) {
                resolve({ success: false, error: errorMsg });
              } else {
                ToastifyAlert(errorMsg, "error");
                setFileData([]);
              }
              return;
            }

            const allLines = text
              .replace(/\r\n?/g, "\n")
              .split("\n")
              .filter((line) => line.trim() !== "");
            if (allLines.length === 0) {
              const errorMsg =
                "File contains no data. Please upload a file with data.";
              if (validateOnly) {
                resolve({ success: false, error: errorMsg });
              } else {
                ToastifyAlert(errorMsg, "error");
                setFileData([]);
              }
              return;
            }

            if (allLines.length <= 1) {
              const errorMsg =
                "File should have data rows in addition to the header. Please upload a file with data.";
              if (validateOnly) {
                resolve({ success: false, error: errorMsg });
              } else {
                ToastifyAlert(errorMsg, "error");
                setFileData([]);
              }
              return;
            }

            // For delimiter detection, use first few lines but avoid multi-line quoted fields
            let sampleText = allLines
              .slice(0, Math.min(5, allLines.length))
              .join("\n");
            const { detectedDelimiter, maxCount } = detectDelimiter(sampleText);

            const delimiterMap = {
              comma: ",",
              "semi-colon": ";",
              tab: "\t",
              pipe: "|",
            };
            const selectedDelimiter = delimiterMap[del] || ",";

            if (selectedDelimiter !== detectedDelimiter && maxCount > 0) {
              const delimiterNames = {
                ",": "comma",
                ";": "semicolon",
                "\t": "tab",
                "|": "pipe",
              };
              if (!delimiterErrorShown.current) {
                setDelimiterError(
                  `Selected delimiter (${delimiterNames[selectedDelimiter] || "unknown"}) ` +
                  `does not match the file's delimiter (${delimiterNames[detectedDelimiter] || "unknown"}). ` +
                  `Please select the correct delimiter and try again.`
                );
                delimiterErrorShown.current = true;
              }
              setFileData([]);
              return;
            }

            let finalDelimiter = detectedDelimiter;
            if (maxCount === 0) {
              if (file.name.toLowerCase().endsWith(".tsv")) {
                finalDelimiter = "\t";
              } else if (file.name.toLowerCase().endsWith(".psv")) {
                finalDelimiter = "|";
              } else if (file.name.toLowerCase().endsWith(".csv")) {
                finalDelimiter = selectedDelimiter;
                const sampleLines = allLines.slice(
                  0,
                  Math.min(5, allLines.length)
                );
                const semicolonCount = sampleLines.reduce(
                  (count, line) => count + (line.split(";").length - 1),
                  0
                );
                const commaCount = sampleLines.reduce(
                  (count, line) => count + (line.split(",").length - 1),
                  0
                );
                if (semicolonCount > commaCount * 2) {
                  finalDelimiter = ";";
                }
              }
            }

            const parsed = [];
            const failed = [];

            // Parse entire CSV content to handle multi-line quoted fields
            console.log("Using new CSV parser with delimiter:", finalDelimiter);
            const csvRows = parseCSVContent(text, finalDelimiter);
            console.log("Parsed rows count:", csvRows.length);

            for (let i = 0; i < csvRows.length; i++) {
              try {
                const parsedLine = csvRows[i];

                if (
                  i > 0 &&
                  parsed.length > 0 &&
                  parsedLine.length !== parsed[0].length
                ) {
                  throw new Error(
                    `Column count mismatch: Expected ${parsed[0].length} columns, got ${parsedLine.length}`
                  );
                }

                const isEmptyRow = parsedLine.every(
                  (cell) => !cell || cell.trim() === ""
                );
                if (isEmptyRow) {
                  // Skip empty rows silently, don't throw error
                  continue;
                }

                parsed.push(parsedLine);
              } catch (error) {
                console.error(`Error parsing row ${i + 1}:`, error);
                failed.push({
                  lineNumber: i + 1,
                  content: csvRows[i] ? csvRows[i].join(",") : "",
                  error: error.message,
                });
              }
            }

            setFailedRows(failed);

            if (parsed.length <= 1 && failed.length > 0) {
              throw new Error("Failed to parse any valid data from the file");
            }

            const headerLength = parsed[0]?.length || 0;
            const hasInconsistentColumns = parsed.some(
              (row) => row.length !== headerLength
            );

            if (hasInconsistentColumns) {
              ToastifyAlert(
                "Inconsistent number of columns in the CSV file. Please check the file format.",
                "error"
              );
              setFileData([]);
              return;
            }

            const headers = parsed[0].map((h) => h.toLowerCase());
            const titleIndex = headers.indexOf("producttitle");
            const upcIndex = headers.findIndex((h) => h === "upc");
            const categoryIndex = headers.indexOf("category");
            const priceIndex = headers.indexOf("price");
            const comparePriceIndex = headers.indexOf("compareprice");

            const dataRows = parsed.slice(1);
            const hasValidData = dataRows.some((row) =>
              row.some((cell) => cell && cell.trim() !== "")
            );

            if (!hasValidData) {
              ToastifyAlert(
                "File should have data. Please upload a file with actual data in the rows.",
                "error"
              );
              setFileData([]);
              return;
            }

            // Duplicate title check removed
            // const titleMap = new Map();
            const upcMap = new Map();
            const validationErrors = [];
            const duplicateLines = new Map();
            const errorCounts = {
              title: 0,
              category: 0,
              price: 0,
              comparePrice: 0,
              duplicateUPC: 0,
            };

            dataRows.forEach((row, index) => {
              const lineNumber = index + 2;
              const rowErrors = [];
              let priceForComparison = null;

              if (titleIndex >= 0) {
                const title = row[titleIndex]?.trim();
                if (!title) {
                  rowErrors.push("Title is required");
                  errorCounts.title++;
                }
              }

              if (categoryIndex >= 0) {
                const category = row[categoryIndex]?.trim();
                if (!category) {
                  rowErrors.push("Category is required");
                  errorCounts.category++;
                }
              }

              if (priceIndex >= 0) {
                const price = parseFloat(row[priceIndex]);
                if (isNaN(price)) {
                  rowErrors.push("Price must be a valid number");
                  errorCounts.price++;
                } else if (price <= 0) {
                  rowErrors.push("Price must be greater than 0");
                  errorCounts.price++;
                } else {
                  priceForComparison = price;
                }
              }

              if (comparePriceIndex >= 0) {
                const comparePriceRaw = row[comparePriceIndex];
                const comparePriceText = comparePriceRaw?.toString().trim();

                if (comparePriceText) {
                  const comparePriceValue = parseFloat(comparePriceText);
                  if (isNaN(comparePriceValue)) {
                    rowErrors.push("Compare Price must be a valid number");
                    errorCounts.comparePrice++;
                  } else if (
                    comparePriceValue > 0 &&
                    priceForComparison !== null &&
                    comparePriceValue <= priceForComparison
                  ) {
                    rowErrors.push("Compare Price must be greater than Price");
                    errorCounts.comparePrice++;
                  }
                }
              }

              if (upcIndex >= 0) {
                const upc = row[upcIndex]?.trim();
                if (upc && upcMap.has(upc)) {
                  const lastLine = upcMap.get(upc);
                  rowErrors.push(
                    `Error - Duplicate UPC (first at line ${lastLine})`
                  );
                  errorCounts.duplicateUPC++;
                  if (!duplicateLines.has(lastLine)) {
                    duplicateLines.set(lastLine, new Set());
                  }
                  duplicateLines
                    .get(lastLine)
                    .add(`Error - Duplicate UPC at line ${lineNumber}`);
                } else if (upc) {
                  upcMap.set(upc, lineNumber);
                }
              }

              if (rowErrors.length > 0) {
                validationErrors.push({
                  lineNumber,
                  content: row.join(","),
                  error: rowErrors.join("; "),
                });
              }
            });

            if (validationErrors.length > 0) {
              setFailedRows((prev) => [...prev, ...validationErrors]);

              const errorSummary = {
                title: 0,
                category: 0,
                price: 0,
                comparePrice: 0,
                duplicateUPC: 0,
              };

              validationErrors.forEach(({ error }) => {
                if (error.includes("Title is required")) errorSummary.title++;
                if (error.includes("Category is required"))
                  errorSummary.category++;
                if (error.includes("Price must be")) errorSummary.price++;
                if (error.includes("Compare Price"))
                  errorSummary.comparePrice++;
                if (error.includes("Error - Duplicate UPC"))
                  errorSummary.duplicateUPC++;
              });

              const summaryRow = {
                lineNumber: "Summary",
                content: "Validation Summary",
                error: [
                  errorSummary.title
                    ? `${errorSummary.title} missing title(s)`
                    : null,
                  errorSummary.category
                    ? `${errorSummary.category} missing category(ies)`
                    : null,
                  errorSummary.price
                    ? `${errorSummary.price} invalid price(s)`
                    : null,
                  errorSummary.comparePrice
                    ? `${errorSummary.comparePrice} invalid compare price(s)`
                    : null,
                  errorSummary.duplicateUPC
                    ? `${errorSummary.duplicateUPC} duplicate UPC(s)`
                    : null,
                ]
                  .filter(Boolean)
                  .join("; "),
              };

              setFailedRows((prev) => [summaryRow, ...prev]);
            }

            if (duplicateLines.size > 0) {
              const newFailedRows = [...failedRows];
              let titleDupCount = 0;
              let upcDupCount = 0;

              duplicateLines.forEach((errors, line) => {
                const errorMessages = Array.from(errors);
                errorMessages.forEach((msg) => {
                  if (msg.includes("title")) titleDupCount++;
                  if (msg.includes("UPC")) upcDupCount++;
                });

                newFailedRows.push({
                  lineNumber: line,
                  content: dataRows[line - 2].join(","),
                  error: errorMessages.join("; "),
                });
              });

              const duplicateSummary = {
                lineNumber: "Note",
                content: "Duplicate Handling",
                error: [
                  // titleDupCount > 0 ? `${titleDupCount} duplicate title(s) - keeping last occurrence` : null,
                  upcDupCount > 0
                    ? `${upcDupCount} duplicate UPC(s) - keeping last occurrence`
                    : null,
                ]
                  .filter(Boolean)
                  .join("; "),
              };

              // Deduplicate errors by error message and content
              const seen = new Set();
              const dedupedRows = [duplicateSummary, ...newFailedRows].filter(
                (row) => {
                  const key = row.error + "|" + row.content;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                }
              );
              if (duplicateSummary.error) {
                setFailedRows(dedupedRows);
              } else {
                setFailedRows(newFailedRows);
              }
            }

            if (!validateOnly) {
              setFileData(parsed);
              processedFiles.current.add(file.name);
              // Clear delimiter error on successful parsing
              setDelimiterError("");
            }
            resolve({ success: true });
          } catch (error) {
            console.error("Error parsing file:", error);
            const errorMsg =
              error.message ||
              "Error parsing file. Please check the file format and try again.";
            if (validateOnly) {
              resolve({ success: false, error: errorMsg });
            } else {
              ToastifyAlert(errorMsg, "error");
              setFileData([]);
              resolve({ success: false, error: errorMsg });
            }
          }
        };

        reader.onerror = (error) => {
          console.error("Error reading file:", error);
          const errorMsg = `Error reading file: ${error.message || "Unknown error"}`;
          if (validateOnly) {
            resolve({ success: false, error: errorMsg });
          } else {
            ToastifyAlert(errorMsg, "error");
            setFileData([]);
            resolve({ success: false, error: errorMsg });
          }
        };

        reader.readAsText(file);
      });
    },
    [del, selectedFile, fileData.length]
  );

  useEffect(() => {
    if (selectedFile) {
      hasShownAutoMapMessage.current = true;

      // Reset delimiter error flag only when delimiter actually changes
      if (previousDelimiter.current !== del) {
        delimiterErrorShown.current = false;
        setDelimiterError(""); // Clear delimiter error message
        previousDelimiter.current = del;
      }

      parseFilePreview(selectedFile, true);
    }
  }, [del, selectedFile, parseFilePreview]);

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!selectedFile) {
        ToastifyAlert("Please select a file to continue", "error");
        return;
      }

      const result = await parseFilePreview(selectedFile, true, true);
      if (!result.success) {
        ToastifyAlert(result.error || "Invalid file format", "error");
        return;
      }

      await parseFilePreview(selectedFile, true);
    }

    if (activeStep === 0 && selectedFile) {
      if (!fileData || fileData.length === 0) {
        ToastifyAlert(
          "File should have data. Please upload a file with data.",
          "error"
        );
        return;
      }
      if (fileData.length <= 1) {
        ToastifyAlert(
          "File should have data rows in addition to the header. Please upload a file with data.",
          "error"
        );
        return;
      }
      const dataRows = fileData.slice(1);
      const hasValidData = dataRows.some((row) =>
        row.some((cell) => cell && cell.trim() !== "")
      );
      if (!hasValidData) {
        ToastifyAlert(
          "File should have data. Please upload a file with actual data in the rows.",
          "error"
        );
        return;
      }
    }

    if (activeStep === 1) {
      // Check for required fields and show error if not mapped
      const requiredFields = [
        "ProductTitle",
        "Category",
        "Price",
        "SKU",
      ];
      const missingRequiredFields = requiredFields.filter(
        (field) => fieldMapping[field] === undefined || fieldMapping[field] === ""
      );
      if (missingRequiredFields.length > 0) {
        ToastifyAlert(
          `Please map the following required fields: ${missingRequiredFields.join(", ")}`,
          "error"
        );
        return;
      }

      const columnIndexCounts = {};
      const duplicateColumns = [];
      Object.entries(fieldMapping).forEach(([systemField, columnIndex]) => {
        if (columnIndex !== undefined && columnIndex !== "") {
          columnIndexCounts[columnIndex] = columnIndexCounts[columnIndex] || [];
          columnIndexCounts[columnIndex].push(systemField);
        }
      });

      Object.entries(columnIndexCounts).forEach(
        ([columnIndex, systemFields]) => {
          if (systemFields.length > 1) {
            const csvColumnName = fileData[0]?.[columnIndex];
            duplicateColumns.push({
              columnName: csvColumnName,
              columnIndex,
              systemFields,
            });
          }
        }
      );

      if (duplicateColumns.length > 0) {
        const errorMessages = duplicateColumns.map(
          (duplicate) =>
            `"${duplicate.columnName}" column is mapped to multiple fields: ${duplicate.systemFields.join(", ")}`
        );
        ToastifyAlert(
          `Same column header has been mapped to multiple columns:\n${errorMessages.join("\n")}`,
          "error"
        );
        ToastifyAlert(`Same column header has been mapped to multiple columns:\n${errorMessages.join("\n")}`, "error");
        return;
      }
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = () => {
    // Clear all form data and reset state
    setSelectedFile(null);
    setFileData([]);
    setFailedRows([]);
    setFieldMapping({});
    setActiveStep(0);
    setIsUploading(false);
    setUploadProgress(0);
    setUploadCompleted(false);
    processedFiles.current.clear();
    hasShownAutoMapMessage.current = false;
    hasShownStandardLayoutMessage.current = false;
    delimiterErrorShown.current = false;

    // Close modal and navigate to import data page
    setShowCancelModal(false);
    navigate("/import-data");
  };

  const handleCancelCancel = () => {
    setShowCancelModal(false);
  };

  const handleUpload = async () => {
    if (!selectedFile || fileData.length === 0) {
      ToastifyAlert("Please select a file first", "error");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const backendFieldMapping = {};
      const convertToBoolean = (value) => {
        if (!value) return false;
        const lowerValue = value.toString().toLowerCase().trim();
        return ["true", "1", "yes", "y", "on", "enabled"].includes(lowerValue);
      };

      const completeFileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            let text = e.target.result || "";
            if (text.charCodeAt(0) === 0xfeff) {
              text = text.slice(1);
            }
            text = text.replace(/\r\n?/g, "\n");

            const CHUNK_SIZE = 1000;
            const booleanFields = [
              "TrackQuantity",
              "DefaultTax",
              "ContinueSelling",
              "OnlinePickup",
              "OnlineDelivery",
            ];
            const processedData = [];

            const delimiterMap = {
              comma: ",",
              "semi-colon": ";",
              tab: "\t",
              pipe: "|",
            };
            const actualDelimiter = delimiterMap[del] || ",";

            // Quote-aware parse: keeps multi-line quoted fields (e.g. a
            // description with an embedded newline) inside one record so a
            // single product never gets split into multiple rows.
            const allRecords = parseCSVRecords(text, actualDelimiter);
            const logicalRecords = allRecords.filter((r) =>
              r.some((cell) => cell && cell.toString().trim() !== "")
            );
            if (logicalRecords.length === 0) {
              throw new Error("No CSV content after normalization");
            }

            const header = logicalRecords[0];
            const headerCols = header.length;
            const dataRecords = logicalRecords.slice(1);

            for (
              let chunkStart = 0;
              chunkStart < dataRecords.length;
              chunkStart += CHUNK_SIZE
            ) {
              const chunkEnd = Math.min(
                chunkStart + CHUNK_SIZE,
                dataRecords.length
              );
              for (let i = chunkStart; i < chunkEnd; i++) {
                const row = dataRecords[i];
                const allEmpty = row.every(
                  (cell) => !cell || cell.toString().trim() === ""
                );
                if (allEmpty) continue;

                let finalRow = row;
                if (row.length !== headerCols) {
                  const repaired = row.map((cell) =>
                    typeof cell === "string"
                      ? cell.replace(/^\s*["']|["']\s*$/g, "").trim()
                      : cell
                  );
                  if (repaired.length === headerCols) {
                    finalRow = repaired;
                  } else {
                    console.warn(
                      `⚠️ Skipping row ${i + 1} due to column count mismatch (expected ${headerCols}, got ${row.length})`
                    );
                    continue;
                  }
                }

                const cleanedRow = finalRow.map((cell) =>
                  typeof cell === "string"
                    ? cell.replace(/\s+/g, " ").trim()
                    : cell
                );

                Object.entries(fieldMapping).forEach(
                  ([systemField, columnIndex]) => {
                    if (
                      booleanFields.includes(systemField) &&
                      cleanedRow[columnIndex] !== undefined
                    ) {
                      const booleanValue = convertToBoolean(
                        cleanedRow[columnIndex]
                      );
                      cleanedRow[columnIndex] = booleanValue;
                    }
                  }
                );

                processedData.push(cleanedRow);
              }

              if (chunkStart + CHUNK_SIZE < dataRecords.length) {
                await new Promise((resolve) => setTimeout(resolve, 10));
              }
            }

            resolve([header, ...processedData]);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error("File read error"));
        reader.readAsText(selectedFile);
      });

      const finalCsvData = completeFileData;

      Object.entries(fieldMapping).forEach(([systemField, columnIndex]) => {
        const actualCsvColumnName = fileData[0]?.[columnIndex];
        if (actualCsvColumnName) {
          const cleanColumnName = actualCsvColumnName
            .trim()
            .replace(/['"]/g, "");
          backendFieldMapping[cleanColumnName] = systemField;
        }
      });

      const delimiterMap = {
        comma: ",",
        "semi-colon": ";",
        tab: "\t",
        pipe: "|",
      };
      const actualDelimiter = delimiterMap[del] || ",";

      const processedCsvContent = finalCsvData
        .map((row) =>
          row
            .map((cell) => {
              if (typeof cell === "boolean") return cell.toString();
              if (cell === null || cell === undefined) return "";
              let cellStr = String(cell);
              if (
                cellStr.includes(actualDelimiter) ||
                cellStr.includes('"') ||
                cellStr.includes("\n") ||
                cellStr.includes("\r")
              ) {
                cellStr = `"${cellStr.replace(/"/g, '""')}"`;
              }
              return cellStr;
            })
            .join(actualDelimiter)
        )
        .join("\n");

      console.log("Generated CSV content:", processedCsvContent);

      const processedFile = new File([processedCsvContent], selectedFile.name, {
        type: "text/csv",
      });
      setProcessedFile(processedFile);
      console.log(
        "DEBUG processedFile after set:",
        processedFile,
        "size:",
        processedFile.size
      );
      console.log(processedFile);

      const formData = new FormData();
      formData.append("file", processedFile);
      formData.append("merchant_id", merchant_id);
      formData.append("field_mapping", JSON.stringify(backendFieldMapping));
      formData.append("duplicate_handling", duplicateHandling);
      formData.append("is_multi_step", "yes");
      formData.append("login_type", userTypeData?.login_type);
      formData.append("token_id", userTypeData?.token_id);
      formData.append("user_id", userTypeData?.user_id);
      formData.append("encoding", encoding);
      formData.append("delimiter", del);
      if (duplicateHandling === "overwrite") {
        formData.append("overwrite", overwrite);
      }

      const response = await axios.post(
        BASE_URL + IMPORT_DATA_ADVANCE,
        withSessionFields(formData),
        {
          headers: { "Content-Type": "multipart/form-data", ...getSessionAuthHeader() },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          },
        }
      );

      if (response.data.status === true || response.data.status === "true") {
        setUploadCompleted(true);
        ToastifyAlert(
          "Your product import has started. You will be notified once it is complete",
          "success"
        );
        if (savePreferences) await savePreferencesToLocalStorage();
        setTimeout(() => {
          setActiveStep(0);
          setSelectedFile(null);
          setFileData([]);
          setFieldMapping({});
          setSavePreferences(false);
          setIsUploading(false);
          setUploadProgress(0);
          setEncoding("UTF-8");
          setDelimeter("comma");
          setDuplicateHandling("skip");
          setOverWrite("productandvariantid");
          setUploadCompleted(false);
          navigate("/import-data");
        }, 3000);
      } else {
        ToastifyAlert(response.data.message || "Import failed", "error");
      }
    } catch (error) {
      console.error("Upload error:", error);
      ToastifyAlert("Upload failed. Please try again.", "error");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "16px", fontWeight: "500" }}
              gutterBottom
            >
              Configure Import Settings
            </Typography>

            <Paper
              className="qv-import-data-upload-zone"
              sx={{
                p: 4,
                border: dragActive ? "2px dashed var(--qv-bd-1976d2, #1976d2)" : "2px dashed var(--qv-bd-cccccc, #ccc)",
                borderRadius: 2,
                textAlign: "center",
                backgroundColor: dragActive ? "var(--qv-bg-f3f8ff, #f3f8ff)" : "var(--qv-bg-fafafa, #fafafa)",
                cursor: "pointer",
                mb: 3,
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              // onClick={() => fileInputRef.current?.click()}
              onClick={() => {
                // Reset the file input value to allow selecting the same file again
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
                fileInputRef.current?.click();
              }}
            >
              <UploadIcon sx={{ fontSize: 48, color: "var(--qv-tx-666666, #666)", mb: 2 }} />
              <Typography variant="h6" sx={{ fontSize: 16 }} gutterBottom>
                Drag and drop file to import
              </Typography>
              <Button variant="contained" sx={{ mt: 2, textTransform: "none" }}>
                Choose File
              </Button>
              <Typography
                variant="body2"
                sx={{ mt: 2, color: "var(--qv-tx-666666, #666)", fontSize: "12px" }}
              >
                Maximum File Size: 20 MB • File Format: CSV
              </Typography>
              {selectedFile && (
                <Chip
                  label={selectedFile.name}
                  color="primary"
                  sx={{ mt: 2 }}
                  onDelete={handleFileRemoval}
                />
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
            </Paper>

            <Alert severity="info" sx={{ mb: 3 }}>
              Download a{" "}
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  window.location.href =
                    BUCKET_BASE_URL + "upload/SampleCSV/SampleProducts.csv";
                }}
              >
                sample file
              </Button>{" "}
              and compare it to your import file to ensure you have the file
              perfect for the import
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontSize: "16px", fontWeight: "500" }}
                gutterBottom
              >
                Duplicate Handling: *
              </Typography>
              <FormControl component="fieldset">
                <Box>
                  <label style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      style={{ cursor: "pointer" }}
                      value="skip"
                      checked={duplicateHandling === "skip"}
                      onChange={(e) => setDuplicateHandling(e.target.value)}
                    />
                    <Typography component="span" sx={{ ml: 1 }}>
                      Skip Duplicates
                    </Typography>
                  </label>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ ml: 3, mb: 2 }}
                  >
                    Retains the items in QuickVee Inventory and does not import
                    the duplicates in the import file.
                  </Typography>
                </Box>
                <Box>
                  <label style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      style={{ cursor: "pointer" }}
                      value="overwrite"
                      checked={duplicateHandling === "overwrite"}
                      onChange={(e) => setDuplicateHandling(e.target.value)}
                    />
                    <Typography component="span" sx={{ ml: 1 }}>
                      Overwrite items
                    </Typography>
                  </label>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ ml: 3, mb: 2 }}
                  >
                    Imports duplicate records in the import file and overwrites
                    the existing items in Quickvee based on the UPC.
                  </Typography>
                </Box>
              </FormControl>
            </Box>

            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>Character Encoding</InputLabel>
              <Select
                sx={{ fontSize: "14px" }}
                value={encoding}
                onChange={(e) => setEncoding(e.target.value)}
                label="Character Encoding"
                MenuProps={{
                  PaperProps: { className: "qv-import-data-menu" },
                }}
              >
                {encodings.map((enc) => (
                  <MenuItem
                    key={enc.value}
                    value={enc.value}
                    sx={{ fontSize: "14px" }}
                  >
                    {enc.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>File Delimiter</InputLabel>
              <Select
                sx={{ fontSize: "14px" }}
                value={del}
                onChange={(e) => setDelimeter(e.target.value)}
                label="File Delimiter"
                MenuProps={{
                  PaperProps: { className: "qv-import-data-menu" },
                }}
              >
                {delimiters.map((d) => (
                  <MenuItem
                    key={d.value}
                    value={d.value}
                    sx={{ fontSize: "14px" }}
                  >
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Delimiter Error Message */}
            {delimiterError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {delimiterError}
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ p: 3 }}>
            {duplicateHandling === "overwrite" && (
              <>
                <Typography
                  variant="h6"
                  sx={{ fontSize: "16px", fontWeight: "500" }}
                  gutterBottom
                >
                  Overwrite products
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 3 }}
                >
                  Choose the field based on which you want to overwrite the
                  items.
                </Typography>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                    <InputLabel>Overwrite</InputLabel>
                    <Select
                      sx={{ fontSize: "14px" }}
                      value={overwrite}
                      onChange={(e) => setOverWrite(e.target.value)}
                      label="Overwrite"
                      MenuProps={{
                        PaperProps: { className: "qv-import-data-menu" },
                      }}
                    >
                      {overWrites.map((ov) => (
                        <MenuItem
                          key={ov.value}
                          value={ov.value}
                          sx={{ fontSize: "14px" }}
                        >
                          {ov.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Typography
              variant="h6"
              sx={{ fontSize: "16px", fontWeight: "500" }}
              gutterBottom
            >
              Map Fields
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Map the columns in your file to the corresponding fields in the
              system.
            </Typography>

            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Required fields:</strong> ProductTitle, Category, Price, and SKU must be mapped before proceeding.
              </Typography>
            </Alert>

            {fileData.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle2"
                      className="qv-import-data-map-col-system"
                      sx={{
                        backgroundColor: "var(--qv-bg-f5f5f5, #f5f5f5)",
                        p: 1.5,
                        borderRadius: 1,
                        textAlign: "center",
                        fontWeight: "bold",
                        color: "var(--qv-tx-666666, #666)",
                        border: "1px solid var(--qv-bd-e0e0e0, #e0e0e0)",
                      }}
                    >
                      QUICKVEE INVENTORY FIELD
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="subtitle2"
                      className="qv-import-data-map-col-file"
                      sx={{
                        backgroundColor: "var(--qv-bg-e3f2fd, #e3f2fd)",
                        p: 1.5,
                        borderRadius: 1,
                        textAlign: "center",
                        fontWeight: "bold",
                        color: "var(--qv-tx-1976d2, #1976d2)",
                        border: "1px solid var(--qv-bd-bbdefb, #bbdefb)",
                      }}
                    >
                      IMPORTED FILE HEADERS
                    </Typography>
                  </Grid>
                </Grid>

                <Paper className="qv-import-data-mapping" sx={{ p: 2 }}>
                  {availableFields.map((systemField, fieldIndex) => {
                    if (
                      duplicateHandling === "overwrite" &&
                      systemField === "Quantity"
                    ) {
                      return null;
                    }
                    const mappedColumnIndex = fieldMapping[systemField];
                    const isRequired = [
                      "ProductTitle",
                      "Price",
                      "Category",
                      "SKU",
                    ].includes(systemField);

                    return (
                      <React.Fragment key={fieldIndex}>
                        <Grid
                          container
                          spacing={2}
                          sx={{ mb: 1.5, alignItems: "center" }}
                        >
                          <Grid item xs={6}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isRequired ? "bold" : "normal",
                                color: isRequired ? "var(--qv-tx-d32f2f, #d32f2f)" : "var(--qv-tx-333333, #333)",
                                fontSize: "14px",
                              }}
                            >
                              {systemField === "UPC" ? "UPC (optional)" : systemField}
                              {isRequired && systemField !== "UPC" ? "*" : ""}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Autocomplete
                              className="qv-import-data-select"
                              size="small"
                              componentsProps={{
                                popper: {
                                  className: "qv-import-data-menu",
                                },
                              }}
                              options={
                                fileData[0]?.map((header, headerIndex) => ({
                                  label: header.replace(/["']/g, ""),
                                  value: headerIndex,
                                })) || []
                              }
                              value={
                                mappedColumnIndex !== undefined
                                  ? {
                                    label:
                                      fileData[0]?.[
                                        mappedColumnIndex
                                      ]?.replace(/["']/g, "") || "",
                                    value: mappedColumnIndex,
                                  }
                                  : null
                              }
                              onChange={(event, newValue) => {
                                const newMapping = { ...fieldMapping };
                                const newWarnings = {
                                  ...fieldValidationWarnings,
                                };

                                if (newValue === null) {
                                  // Prevent clearing required fields
                                  if (isRequired && systemField !== "UPC") {
                                    ToastifyAlert(
                                      `${systemField} is a required field and cannot be unmapped`,
                                      "error"
                                    );
                                    return;
                                  }
                                  newMapping[systemField] = "";
                                  delete newWarnings[systemField];
                                } else {
                                  newMapping[systemField] = newValue.value;

                                  // Validate the mapping
                                  const warnings = validateFieldMapping(
                                    systemField,
                                    newValue.value,
                                    fileData
                                  );
                                  if (warnings) {
                                    newWarnings[systemField] = warnings;
                                  } else {
                                    delete newWarnings[systemField];
                                  }
                                }

                                setFieldMapping(newMapping);
                                setFieldValidationWarnings(newWarnings);
                              }}
                              clearOnEscape
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Select"
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      fontSize: "14px",
                                    },
                                  }}
                                />
                              )}
                              sx={{
                                "& .MuiAutocomplete-option": {
                                  fontSize: "12px",
                                },
                              }}
                            />
                          </Grid>
                        </Grid>

                        {/* Display validation warnings */}
                        {fieldValidationWarnings[systemField] && (
                          <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12}>
                              <Alert
                                severity="warning"
                                sx={{ fontSize: "12px" }}
                              >
                                <strong>{systemField} Validation:</strong>
                                <ul
                                  style={{
                                    margin: "4px 0",
                                    paddingLeft: "16px",
                                  }}
                                >
                                  {fieldValidationWarnings[systemField].map(
                                    (warning, idx) => (
                                      <li
                                        key={idx}
                                        style={{ fontSize: "11px" }}
                                      >
                                        {warning}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </Alert>
                            </Grid>
                          </Grid>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Paper>
              </Box>
            )}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={savePreferences}
                    onChange={(e) => setSavePreferences(e.target.checked)}
                    color="primary"
                  />
                }
                label="Save these selections for use during future imports."
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "var(--qv-tx-666666, #666)",
                  },
                }}
              />
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Review and Upload
            </Typography>

            <Paper className="qv-import-data-review-summary" sx={{ p: 2, mb: 3, backgroundColor: "var(--qv-bg-f8f9fa, #f8f9fa)" }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>File:</strong> {selectedFile?.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Size:</strong>{" "}
                    {processedFile
                      ? processedFile.size > 1024 * 1024
                        ? (processedFile.size / 1024 / 1024).toFixed(2) + " MB"
                        : (processedFile.size / 1024).toFixed(2) + " KB"
                      : "0.00 KB"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Encoding:</strong> {encoding}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Duplicate Handling:</strong> {duplicateHandling}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {(() => {
              const totalDataRows =
                fileData.length > 1 ? fileData.length - 1 : 0;
              const failedDataRows = failedRows.filter(
                (row) => row.lineNumber && !isNaN(Number(row.lineNumber))
              );
              const readyToImport = totalDataRows - failedDataRows.length;

              if (Object.keys(fieldMapping).length === 0) {
                return (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    None of the rows can be imported
                  </Alert>
                );
              } else if (readyToImport > 0) {
                return (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {readyToImport} item{readyToImport !== 1 ? "s" : ""} ready
                    to be imported
                  </Alert>
                );
              }
              return null;
            })()}

            {(() => {
              // Only count failed rows that are actual data rows, not summary/info rows
              const failedDataRows = failedRows.filter(
                (row) => row.lineNumber && !isNaN(Number(row.lineNumber))
              );

              if (failedDataRows.length === 0) {
                return null;
              }

              return (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Alert
                        severity="warning"
                        sx={{
                          p: 0,
                          pl: 1,
                          pr: 1,
                          display: "inline-flex",
                          alignItems: "center",
                        }}
                      >
                        ⚠️ {failedDataRows.length} row
                        {failedDataRows.length !== 1 ? "s" : ""} failed to parse
                      </Alert>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          const headerRow =
                            fileData.length > 0 ? fileData[0] : [];

                          // Define the specific header format you want
                          const customHeader = [
                            "Error",
                            "ProductTitle",
                            "Category",
                            "UPC",
                          ];

                          const csvContent = [
                            customHeader.map((cell) => `"${cell}"`).join(","),
                            ...failedRows
                              .filter(
                                (row) => row.content !== "Duplicate Handling"
                              )
                              .map((row) => {
                                // Parse the content if it's a CSV string, otherwise treat as array
                                let contentArray;
                                if (typeof row.content === "string") {
                                  // Split CSV string and clean up quotes
                                  contentArray = row.content
                                    .split(",")
                                    .map((cell) =>
                                      cell.trim().replace(/^["']|["']$/g, "")
                                    );
                                } else if (Array.isArray(row.content)) {
                                  contentArray = row.content;
                                } else {
                                  contentArray = [row.content || ""];
                                }

                                // Map original data to the specific format: Error, ProductTitle, Category, UPC, ProductDescription
                                // Original CSV order: ProductTitle(0), ProductDescription(1), Category(2), Price(3), ComparePrice(4), CostPrice(5), Variant1Name(6), Variant1Value(7), Variant2Name(8), Variant2Value(9), Variant3Name(10), Variant3Value(11), Quantity(12), TrackQuantity(13), ContinueSelling(14), DefaultTax(15), SKU(16), UPC(17), FinalOutputUPC(18), Error(19)
                                const productTitle = contentArray[0] || "";
                                const productDescription =
                                  contentArray[1] || "";
                                const category = contentArray[2] || "";
                                const upc = contentArray[17] || "";
                                // Clean up error message by removing "Last occurrence of duplicates:" prefix
                                let error = row.error || "";
                                if (
                                  error.startsWith(
                                    "Last occurrence of duplicates: "
                                  )
                                ) {
                                  error = error.replace(
                                    "Last occurrence of duplicates: ",
                                    ""
                                  );
                                }

                                const customRow = [
                                  error,
                                  productTitle,
                                  category,
                                  upc,
                                  productDescription,
                                ];

                                return customRow
                                  .map(
                                    (cell) =>
                                      `"${cell ? cell.toString().replace(/"/g, '""') : ""}"`
                                  )
                                  .join(",");
                              }),
                          ].join("\n");
                          const blob = new Blob([csvContent], {
                            type: "text/csv",
                          });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = "skipped_rows.csv";
                          a.click();
                          window.URL.revokeObjectURL(url);
                        }}
                      >
                        📥 Download skipped rows
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() =>
                          setShowSkippedDetails(!showSkippedDetails)
                        }
                      >
                        {showSkippedDetails
                          ? "Hide Details ▲"
                          : "View Details ▼"}
                      </Button>
                    </Box>
                  </Box>
                  {showSkippedDetails &&
                    failedRows.filter(
                      (row) => row.content !== "Duplicate Handling"
                    ).length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          backgroundColor: "var(--qv-bg-f8f9fa, #f8f9fa)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" gutterBottom>
                          Failed to parse{" "}
                          {(() => {
                            const failedDataRows = failedRows.filter(
                              (row) =>
                                row.lineNumber && !isNaN(Number(row.lineNumber))
                            );
                            return failedDataRows.length;
                          })()}{" "}
                          row
                          {(() => {
                            const failedDataRows = failedRows.filter(
                              (row) =>
                                row.lineNumber && !isNaN(Number(row.lineNumber))
                            );
                            return failedDataRows.length !== 1 ? "s" : "";
                          })()}
                          :
                        </Typography>
                        {failedRows
                          .filter((row) => row.content !== "Duplicate Handling")
                          .slice(0, 10)
                          .map((failedRow, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                mb: 2,
                                p: 1,
                                backgroundColor: "var(--qv-bg-ffffff, #fff)",
                                borderRadius: 1,
                                border: "1px solid var(--qv-bd-eeeeee, #eee)",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  wordBreak: "break-word",
                                  lineHeight: 1.4,
                                  flex: 1,
                                  maxWidth: 500,
                                  pr: 2,
                                }}
                              >
                                {(fileData[0]?.[0]?.replace(/["']/g, "") ||
                                  "Product Title") +
                                  " - " +
                                  (failedRow.content.split(",")[0] ||
                                    "Untitled Product")}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ minWidth: 200 }}
                              >
                                {failedRow.error}
                              </Typography>
                            </Box>
                          ))}
                        {failedRows.filter(
                          (row) => row.content !== "Duplicate Handling"
                        ).length > 10 && (
                            <Typography variant="caption" color="text.secondary">
                              ... and{" "}
                              {failedRows.filter(
                                (row) => row.content !== "Duplicate Handling"
                              ).length - 10}{" "}
                              more failed rows
                            </Typography>
                          )}
                      </Box>
                    )}
                </Box>
              );
            })()}

            {(() => {
              // Get all CSV columns that are NOT mapped to any system field
              const unmappedCsvColumns = fileData[0]
                ? fileData[0].filter((columnName, index) => {
                  // Check if this CSV column index is mapped to any system field
                  const isMapped = Object.values(fieldMapping).includes(index);
                  return !isMapped;
                })
                : [];

              // Ignore ProductId/VariantId if they are the only unmapped headers
              const ignoredColumns = ["productid", "variantid"];
              const significantUnmappedColumns = unmappedCsvColumns.filter(
                (columnName) =>
                  !ignoredColumns.includes(columnName?.trim().toLowerCase())
              );

              const unmappedFieldsCount = significantUnmappedColumns.length;

              if (unmappedFieldsCount === 0) {
                return null;
              }

              return (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Alert
                      severity="warning"
                      sx={{
                        p: 0,
                        pl: 1,
                        pr: 1,
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      ⚠️ Unmapped CSV Columns - {unmappedFieldsCount}
                    </Alert>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        setShowUnmappedDetails(!showUnmappedDetails)
                      }
                    >
                      {showUnmappedDetails
                        ? "Hide Details ▲"
                        : "View Details ▼"}
                    </Button>
                  </Box>
                  {showUnmappedDetails && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: "var(--qv-bg-f8f9fa, #f8f9fa)",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" gutterBottom>
                        The following columns in your CSV file have not been
                        mapped to any QuickVee Inventory field. The data in
                        these columns will be ignored during the import.
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        {significantUnmappedColumns.map((columnName, idx) => (
                          <Box key={idx} sx={{ mb: 0.5 }}>
                            <Typography variant="body2">
                              • {columnName}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })()}

            {isUploading && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Uploading... {uploadProgress}%
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    return () => {
      setSelectedFile(null);
      setFileData([]);
      setFieldMapping({});
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
  }, []);

  return (
    <Box className="qv-import-data-panel" sx={{ width: "50%", margin: "0 auto", paddingBottom: "100px" }}>
      <Paper className="qv-import-data-stepper" sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step, index) => {
            const isCompleted =
              index < activeStep || (index === 2 && uploadCompleted);
            return (
              <Step key={step.label} completed={isCompleted}>
                <StepLabel
                  icon={step.icon}
                  sx={{
                    "& .MuiStepLabel-iconContainer .MuiSvgIcon-root": {
                      color: isCompleted
                        ? "var(--qv-tx-4caf50, #4caf50)"
                        : index === activeStep
                          ? "var(--qv-tx-1976d2, #1976d2)"
                          : "#bdbdbd",
                    },
                  }}
                >
                  {step.label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      <Paper className="qv-import-data-content" sx={{ minHeight: 400 }}>{renderStepContent()}</Paper>

      <Box
        className="qv-import-data-footer"
        sx={{
          position: "fixed",
          bottom: 0,
          right: 0,
          width: isMenuOpenRedux ? "calc(100% - 250px)" : "calc(100% - 64px)",
          backgroundColor: "var(--qv-bg-ffffff, #fff)",
          display: "flex",
          justifyContent: "center",
          py: 2,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "50%",
          }}
        >
          <Box>
            <Button
              onClick={handleCancelClick}
              variant="outlined"
              color="error"
              disabled={!selectedFile}
              sx={{
                textTransform: "none",
                "&.Mui-disabled": {
                  color: "var(--qv-tx-a0x0x0x260, rgba(0, 0, 0, 0.26))",
                  borderColor: "var(--qv-bd-a0x0x0x120, rgba(0, 0, 0, 0.12))",
                },
              }}
            >
              Cancel
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            {/* Show validation warning message */}
            {activeStep === 1 &&
              Object.keys(fieldValidationWarnings).length > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "error.main",
                    fontSize: "0.875rem",
                    textAlign: "right",
                  }}
                >
                  Please fix validation warnings before proceeding
                </Typography>
              )}

            <Box sx={{ display: "flex", gap: 2 }}>
              {activeStep > 0 && (
                <Button
                  sx={{ textTransform: "none" }}
                  onClick={handleBack}
                  variant="outlined"
                >
                  ← Previous
                </Button>
              )}

              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={
                    (activeStep === 0 && !selectedFile) ||
                    (activeStep === 0 &&
                      selectedFile &&
                      fileData.length === 0) ||
                    (activeStep === 1 &&
                      Object.keys(fieldValidationWarnings).length > 0) ||
                    (activeStep === 1 &&
                      ["ProductTitle", "Category", "Price", "SKU", "UPC"].some(
                        (field) => fieldMapping[field] === undefined || fieldMapping[field] === ""
                      ))
                  }
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    textTransform: "none",
                    "&.Mui-disabled": {
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      opacity: 0.5,
                    },
                  }}
                >
                  Next →
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={
                    isUploading || Object.keys(fieldMapping).length === 0
                  }
                  startIcon={<SuccessIcon />}
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    textTransform: "none",
                    "&.Mui-disabled": {
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      opacity: 0.5,
                    },
                  }}
                >
                  Import
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Cancel Confirmation Modal */}
      <Dialog
        className="qv-import-data-dialog"
        open={showCancelModal}
        onClose={handleCancelCancel}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
        PaperProps={{ className: "qv-import-data-dialog-paper" }}
      >
        <DialogTitle id="cancel-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel? All uploaded CSV data and progress
            will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelCancel}
            color="primary"
            sx={{ textTransform: "none" }}
          >
            No, Continue
          </Button>
          <Button
            onClick={handleCancelConfirm}
            color="error"
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MultiStepImport;
